import React, { useState, useMemo, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsDoctors,
  useUpdateDoctor,
  useDoctorWeekDayHours,
  useUpdateDoctorWeekDayHours,
  useAutofillDoctorWeekDayHours,
} from '@/v2/hooks/settings/useSettingsDoctors'
import { useSettingsAppointmentTypes } from '@/v2/hooks/settings/useSettingsAppointmentTypes'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import { usStateOptions } from '@/v2/constants/usStates'
import {
  SettingsPage,
  SettingsEmptyState,
  CardGrid,
  Card,
  CardTitle,
  CheckboxGroup,
} from '@/v2/components/settings/shared'

const AppointmentTypesSection = styled.div`
  margin-bottom: 1rem;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`

const SectionTitle = styled.h5`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Count = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`

const AppointmentTypesList = styled.div`
  padding-left: 1.5rem;
`

const AppointmentTypesText = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
`

const DoctorStatusBadge = styled.div`
  background-color: #fef2f2;
  color: #dc2626;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.5rem;
  width: fit-content;
  margin-left: auto;
`

const WarningBox = styled.div`
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #92400e;
  font-size: 0.875rem;
`

const ScheduleTabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1rem;
`

const ScheduleTab = styled.button`
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#3b82f6' : '#6b7280')};
  border-bottom-color: ${({ $active }) =>
    $active ? '#3b82f6' : 'transparent'};

  &:hover {
    color: #3b82f6;
  }
`

const DayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`

const DayLabel = styled.div`
  width: 90px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  text-transform: capitalize;
  flex-shrink: 0;
`

const TimeFieldsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const TimeSeparator = styled.span`
  font-size: 0.8rem;
  color: #9ca3af;
`

// The API rejects a license number without a state and vice versa.
const licenseErrors = ({ licenseNumber, licenseState }) => ({
  licenseNumber:
    licenseState && !licenseNumber.trim() ? 'Enter the license number' : null,
  licenseState:
    licenseNumber.trim() && !licenseState
      ? 'Select the state that issued the license'
      : null,
})

const ScheduleSection = ({ doctorId }) => {
  const { weekDayHours, isLoading: scheduleLoading } = useDoctorWeekDayHours(
    doctorId
  )
  const {
    updateWeekDayHours,
    isPending: isUpdatingSchedule,
  } = useUpdateDoctorWeekDayHours()
  const {
    autofillWeekDayHours,
    isPending: isAutofilling,
  } = useAutofillDoctorWeekDayHours()
  const { onOpenSnackbar } = useSnackbarContext()

  const [activeTab, setActiveTab] = useState('on_site')
  const [localSchedule, setLocalSchedule] = useState(null)

  useEffect(() => {
    if (weekDayHours) {
      setLocalSchedule({
        on_site: (weekDayHours.onSite || []).map(h => ({ ...h })),
        virtual: (weekDayHours.virtual || []).map(h => ({ ...h })),
      })
    }
  }, [weekDayHours])

  const handleDayFieldChange = useCallback((type, dayId, field, value) => {
    setLocalSchedule(prev => {
      if (!prev) return prev
      return {
        ...prev,
        [type]: prev[type].map(h =>
          h.id === dayId ? { ...h, [field]: value } : h
        ),
      }
    })
  }, [])

  const handleToggleOpen = useCallback((type, dayId, checked) => {
    setLocalSchedule(prev => {
      if (!prev) return prev
      return {
        ...prev,
        [type]: prev[type].map(h =>
          h.id === dayId
            ? {
                ...h,
                isOpen: checked,
                ...(checked ? {} : { hasBreak: false }),
              }
            : h
        ),
      }
    })
  }, [])

  const handleToggleBreak = useCallback((type, dayId, checked) => {
    setLocalSchedule(prev => {
      if (!prev) return prev
      return {
        ...prev,
        [type]: prev[type].map(h =>
          h.id === dayId ? { ...h, hasBreak: checked } : h
        ),
      }
    })
  }, [])

  const handleSaveSchedule = async () => {
    if (!localSchedule) return

    const buildPayload = hours => {
      const result = {}
      hours.forEach(h => {
        result[h.id] = {
          is_open: h.isOpen,
          start_at: h.startAt,
          stops_at: h.stopsAt,
          has_break: h.hasBreak,
          break_from: h.breakFrom,
          break_to: h.breakTo,
          appointment_type_ids: h.appointmentTypeIds || [],
        }
      })
      return result
    }

    try {
      await updateWeekDayHours({
        doctorId,
        weekDayHours: {
          on_site: buildPayload(localSchedule.on_site),
          virtual: buildPayload(localSchedule.virtual),
        },
      })
      onOpenSnackbar('Schedule updated successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to update schedule', 'error')
    }
  }

  const handleAutofill = async () => {
    try {
      await autofillWeekDayHours(doctorId)
      onOpenSnackbar('Schedule autofilled successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to autofill schedule', 'error')
    }
  }

  if (scheduleLoading || !localSchedule) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 3,
          color: '#6b7280',
          fontSize: '0.875rem',
        }}
      >
        Loading schedule...
      </Box>
    )
  }

  const currentHours = localSchedule[activeTab] || []

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography bold fontSize="1rem">
          Weekly Schedule
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleAutofill}
            disabled={isAutofilling}
          >
            {isAutofilling ? 'Autofilling...' : 'Autofill'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveSchedule}
            disabled={isUpdatingSchedule}
          >
            {isUpdatingSchedule ? 'Saving...' : 'Save Schedule'}
          </Button>
        </Box>
      </Box>

      <ScheduleTabBar>
        <ScheduleTab
          $active={activeTab === 'on_site'}
          onClick={() => setActiveTab('on_site')}
        >
          On-Site Hours
        </ScheduleTab>
        <ScheduleTab
          $active={activeTab === 'virtual'}
          onClick={() => setActiveTab('virtual')}
        >
          Virtual Hours
        </ScheduleTab>
      </ScheduleTabBar>

      <Box>
        {currentHours.map(dayHour => (
          <DayRow key={dayHour.id}>
            <DayLabel>{dayHour.day}</DayLabel>

            <Box sx={{ flexShrink: 0 }}>
              <CheckboxField
                label="Open"
                checked={dayHour.isOpen}
                onChange={e =>
                  handleToggleOpen(activeTab, dayHour.id, e.target.checked)
                }
              />
            </Box>

            {dayHour.isOpen && (
              <TimeFieldsRow>
                <TextField
                  type="time"
                  value={dayHour.startAt || ''}
                  onChange={val =>
                    handleDayFieldChange(activeTab, dayHour.id, 'startAt', val)
                  }
                  size="small"
                />
                <TimeSeparator>to</TimeSeparator>
                <TextField
                  type="time"
                  value={dayHour.stopsAt || ''}
                  onChange={val =>
                    handleDayFieldChange(activeTab, dayHour.id, 'stopsAt', val)
                  }
                  size="small"
                />

                <Box sx={{ flexShrink: 0, ml: 1 }}>
                  <CheckboxField
                    label="Break"
                    checked={dayHour.hasBreak}
                    onChange={e =>
                      handleToggleBreak(activeTab, dayHour.id, e.target.checked)
                    }
                  />
                </Box>

                {dayHour.hasBreak && (
                  <>
                    <TextField
                      type="time"
                      value={dayHour.breakFrom || ''}
                      onChange={val =>
                        handleDayFieldChange(
                          activeTab,
                          dayHour.id,
                          'breakFrom',
                          val
                        )
                      }
                      size="small"
                    />
                    <TimeSeparator>to</TimeSeparator>
                    <TextField
                      type="time"
                      value={dayHour.breakTo || ''}
                      onChange={val =>
                        handleDayFieldChange(
                          activeTab,
                          dayHour.id,
                          'breakTo',
                          val
                        )
                      }
                      size="small"
                    />
                  </>
                )}
              </TimeFieldsRow>
            )}
          </DayRow>
        ))}
      </Box>
    </Box>
  )
}

const Doctors = () => {
  const {
    doctors: rawDoctors,
    isLoading: doctorsLoading,
  } = useSettingsDoctors()
  const {
    appointmentTypes: rawAppointmentTypes,
    isLoading: apptTypesLoading,
  } = useSettingsAppointmentTypes()
  const { updateDoctor, isPending: isSaving } = useUpdateDoctor()
  const { onOpenSnackbar } = useSnackbarContext()

  const loading = doctorsLoading || apptTypesLoading

  const doctors = useMemo(
    () =>
      (rawDoctors || []).map(d => ({
        id: d.id,
        firstName: d.firstName || '',
        lastName: d.lastName || '',
        fullName: d.fullName || `${d.firstName} ${d.lastName}`,
        appointmentTypes: (d.appointmentTypes || []).map(at => at.title),
        appointmentTypeObjects: d.appointmentTypes || [],
        npi: d.npi || '',
        taxId: d.taxId || '',
        taxonomyCodes: d.taxonomyCodes || [],
        licenseNumber: d.licenseNumber || '',
        licenseState: d.licenseState || '',
        active: d.active !== undefined ? d.active : true,
      })),
    [rawDoctors]
  )

  const appointmentTypes = useMemo(
    () =>
      (rawAppointmentTypes || []).map(at => ({ id: at.id, title: at.title })),
    [rawAppointmentTypes]
  )

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForEdit,
    close,
    updateField,
    setFormData,
  } = useFormModal({
    appointmentTypeIds: [],
    npi: '',
    taxId: '',
    taxonomyCodes: [],
    licenseNumber: '',
    licenseState: '',
  })

  const licenseError = licenseErrors(formData)
  const hasLicenseError = !!(
    licenseError.licenseNumber || licenseError.licenseState
  )

  const handleEditDoctor = doctor => {
    openForEdit(doctor, d => ({
      appointmentTypeIds: (d.appointmentTypeObjects || []).map(at => at.id),
      npi: d.npi,
      taxId: d.taxId,
      taxonomyCodes: d.taxonomyCodes || [],
      licenseNumber: d.licenseNumber,
      licenseState: d.licenseState,
    }))
  }

  const handleSubmit = async () => {
    try {
      await updateDoctor({
        id: editingItem.id,
        npi: formData.npi,
        tax_id: formData.taxId,
        taxonomy_codes: formData.taxonomyCodes,
        license_number: formData.licenseNumber.trim(),
        license_state: formData.licenseState,
        appointment_type_ids: formData.appointmentTypeIds,
      })

      onOpenSnackbar('Doctor updated successfully', 'success')
      close()
    } catch (error) {
      onOpenSnackbar('Failed to update doctor', 'error')
    }
  }

  const handleAppointmentTypeToggle = appointmentTypeId => {
    setFormData(prev => ({
      ...prev,
      appointmentTypeIds: prev.appointmentTypeIds.includes(appointmentTypeId)
        ? prev.appointmentTypeIds.filter(id => id !== appointmentTypeId)
        : [...prev.appointmentTypeIds, appointmentTypeId],
    }))
  }

  return (
    <SettingsPage
      title="Doctors"
      subtitle="Manage doctor profiles, appointment types, and schedules"
      loading={loading}
    >
      {doctors.length === 0 ? (
        <SettingsEmptyState
          icon={
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 9c.83 0 1.5.67 1.5 1.5S21.83 14 21 14s-1.5-.67-1.5-1.5S20.17 11 21 11zm-6 8c-2.33 0-4.31-1.46-5.11-3.5L8.54 15H8c-2.76 0-5-2.24-5-5s2.24-5 5-5v2c-1.66 0-3 1.34-3 3s1.34 3 3 3h.54l1.35.5C10.69 16.54 12.67 18 15 18c2.33 0 4.31-1.46 5.11-3.5L21.46 15H22c2.76 0 5-2.24 5-5s-2.24-5-5-5v-2c2.76 0 5 2.24 5 5s-2.24 5 5 5h-.54l-1.35-.5C13.31 7.46 11.33 6 9 6c-2.33 0-4.31 1.46-5.11 3.5L2.54 9H2c-2.76 0-5 2.24-5 5s2.24 5 5 5v2c-2.76 0-5-2.24-5-5s2.24-5 5-5z" />
            </svg>
          }
          heading="No doctors found"
          description={
            <>
              Doctors are created by inviting users with the{' '}
              <strong>Doctor</strong> role from the{' '}
              <strong>Users and permissions</strong> page.
            </>
          }
        />
      ) : (
        <CardGrid $minWidth="400px">
          {doctors.map(doctor => (
            <Card key={doctor.id} onClick={() => handleEditDoctor(doctor)}>
              <CardTitle $mb="1rem">
                Dr. {doctor.firstName} {doctor.lastName}
              </CardTitle>

              <AppointmentTypesSection>
                <SectionHeader>
                  <SectionTitle>
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                    </svg>
                    Appointment types
                  </SectionTitle>
                  <Count>
                    {doctor.appointmentTypes.length}/{appointmentTypes.length}
                  </Count>
                </SectionHeader>

                <AppointmentTypesList>
                  <AppointmentTypesText>
                    {doctor.appointmentTypes.join(', ') ||
                      'No appointment types assigned'}
                  </AppointmentTypesText>
                </AppointmentTypesList>

                {!doctor.active && (
                  <DoctorStatusBadge>INACTIVE</DoctorStatusBadge>
                )}
              </AppointmentTypesSection>
            </Card>
          ))}
        </CardGrid>
      )}

      <Modal
        isOpen={showModal}
        title={`Dr. ${editingItem?.firstName} ${editingItem?.lastName}`}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel="Save"
        cancelLabel="Cancel"
        submitDisabled={
          isSaving || appointmentTypes.length === 0 || hasLicenseError
        }
        loading={isSaving}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography bold fontSize="1rem">
              Types of appointments
            </Typography>
            <Box sx={{ mt: 1 }}>
              {appointmentTypes.length === 0 ? (
                <WarningBox>
                  You can't edit doctors without creating appointment types
                  first. Click here to create one.
                </WarningBox>
              ) : (
                <CheckboxGroup>
                  {appointmentTypes.map(appointmentType => (
                    <CheckboxField
                      key={appointmentType.id}
                      label={appointmentType.title}
                      checked={formData.appointmentTypeIds.includes(
                        appointmentType.id
                      )}
                      onChange={() =>
                        handleAppointmentTypeToggle(appointmentType.id)
                      }
                    />
                  ))}
                </CheckboxGroup>
              )}
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Insurance Information
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                fullWidth
                label="NPI"
                value={formData.npi}
                onChange={val => updateField('npi', val)}
                placeholder="National Provider Identifier"
              />
              <TextField
                fullWidth
                label="Tax ID"
                value={formData.taxId}
                onChange={val => updateField('taxId', val)}
                placeholder="Tax Identification Number"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Taxonomy Codes"
                value={(formData.taxonomyCodes || []).join(', ')}
                onChange={val =>
                  updateField(
                    'taxonomyCodes',
                    val
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean)
                  )
                }
                placeholder="Comma-separated codes (e.g., 1223G0001X, 122300000X)"
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                mt: 2,
              }}
            >
              <TextField
                fullWidth
                label="License Number"
                value={formData.licenseNumber}
                onChange={val => updateField('licenseNumber', val)}
                placeholder="Dental license number"
                error={licenseError.licenseNumber}
              />
              <SelectField
                fullWidth
                noMargin
                label="License State"
                placeholder="Select State"
                options={usStateOptions}
                value={formData.licenseState}
                onChange={val => updateField('licenseState', val)}
                error={licenseError.licenseState}
              />
            </Box>
          </Box>

          {editingItem && <ScheduleSection doctorId={editingItem.id} />}
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default Doctors
